import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { FileText, Target, Edit, Trash2, ClipboardList, Loader2, Crosshair, Share2 } from "lucide-react";
import { Progress } from "../ui/progress";

// Extract focus area and goal from plan title/description
function parsePlanMeta(plan) {
  const focusAreas = [];
  const goals = [];

  // Check focus_areas field first
  if (plan.focus_areas?.length) {
    focusAreas.push(...plan.focus_areas);
  }

  // Check training_goals field
  if (plan.training_goals?.length) {
    goals.push(...plan.training_goals.map(g => g.replace(/_/g, ' ')));
  }

  // Parse from description: "Focus areas: confidence, focus"
  const focusMatch = plan.description?.match(/focus\s*areas?:\s*([^.]+)/i);
  if (focusMatch && !focusAreas.length) {
    focusAreas.push(...focusMatch[1].split(',').map(s => s.trim()).filter(Boolean));
  }

  // Parse goal from description: "achieve your goal: ..."
  const goalMatch = plan.description?.match(/(?:achieve your\s+)?(?:goal|objective):\s*([^.]+)/i);
  if (goalMatch && !goals.length) goals.push(goalMatch[1].trim());

  // Parse from title: "Training Plan: Goal Name - Focus Area"
  const titleMatch = plan.title?.match(/Training Plan:\s*(.+?)\s*-\s*(.+)/i);
  if (titleMatch) {
    if (!goals.length) goals.push(titleMatch[1].trim());
    if (!focusAreas.length) focusAreas.push(titleMatch[2].trim());
  }

  return { focusAreas: focusAreas.slice(0, 3), goals: goals.slice(0, 2) };
}

export function PlanCard({ plan, canEdit, onSelect, onEdit, onDelete, onShare, completionData }) {
  const isGenerating = plan.generation_status === 'generating';
  const isFailed = plan.generation_status === 'failed';
  const { focusAreas, goals } = parsePlanMeta(plan);

  const totalDays = (plan.duration_weeks || 1) * 7;
  const completedDays = completionData?.total_completed || 0;
  const progressPct = totalDays > 0 ? Math.min(100, Math.round((completedDays / totalDays) * 100)) : 0;

  return (
    <Card
      data-testid={`plan-card-${plan.id}`}
      onClick={() => !isGenerating && onSelect(plan)}
      className={`border-0 shadow-md transition-all duration-300 group relative cursor-pointer ${
        isGenerating
          ? 'bg-gradient-to-br from-white to-amber-50/30 animate-pulse'
          : isFailed
          ? 'bg-gradient-to-br from-white to-red-50/30'
          : 'bg-gradient-to-br from-white to-emerald-50/30 hover:shadow-xl active:scale-[0.98]'
      }`}
    >
      <CardHeader className="p-3 sm:p-4 pb-1 sm:pb-2">
        <div className="flex items-start justify-between">
          <div className={`p-1.5 sm:p-2 rounded-lg ${isGenerating ? 'bg-gradient-to-br from-amber-100 to-yellow-100' : 'bg-gradient-to-br from-emerald-100 to-teal-100'}`}>
            {isGenerating ? <Loader2 className="w-4 h-4 sm:w-5 sm:h-5 text-amber-600 animate-spin" /> : <FileText className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600" />}
          </div>
          <div className="flex items-center gap-1.5">
            <span className="text-[9px] sm:text-[10px] text-gray-400 font-medium">
              {new Date(plan.created_at).toLocaleDateString()}
            </span>
            <div className="flex gap-0.5 md:opacity-0 md:group-hover:opacity-100 transition-opacity">
              {!isGenerating && onShare && (
                <Button variant="ghost" size="sm"
                  data-testid={`share-plan-btn-${plan.id}`}
                  onClick={(e) => { e.stopPropagation(); onShare(plan); }}
                  className="h-7 w-7 p-0 text-blue-600 hover:bg-blue-100" title="Share">
                  <Share2 className="w-3.5 h-3.5" />
                </Button>
              )}
              {canEdit && (
                <>
                  <Button variant="ghost" size="sm"
                    onClick={(e) => { e.stopPropagation(); onEdit(plan); }}
                    className="h-7 w-7 p-0 text-emerald-600 hover:bg-emerald-100" title="Edit">
                    <Edit className="w-3.5 h-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm"
                    onClick={(e) => { e.stopPropagation(); onDelete(plan); }}
                    className="h-7 w-7 p-0 text-red-600 hover:bg-red-100" title="Delete">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
        <div>
          <CardTitle className={`text-sm sm:text-base leading-tight transition-colors ${isGenerating ? 'text-amber-800' : 'text-emerald-900 group-hover:text-emerald-600'}`}>
            {plan.title}
          </CardTitle>
          {/* Focus Area + Goal Tags */}
          {(focusAreas.length > 0 || goals.length > 0) && (
            <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`plan-meta-${plan.id}`}>
              {focusAreas.map((fa, i) => (
                <span key={`fa-${i}`} className="inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] px-1.5 py-0.5 bg-violet-50 text-violet-700 rounded-full font-semibold capitalize">
                  <Crosshair className="w-2.5 h-2.5" />{fa.replace(/_/g, ' ')}
                </span>
              ))}
              {goals.map((g, i) => (
                <span key={`g-${i}`} className="inline-flex items-center gap-0.5 text-[9px] sm:text-[10px] px-1.5 py-0.5 bg-amber-50 text-amber-700 rounded-full font-semibold">
                  <Target className="w-2.5 h-2.5" />{g.length > 40 ? g.slice(0, 40) + '...' : g}
                </span>
              ))}
            </div>
          )}
        </div>
      </CardHeader>
      <CardContent className="p-3 sm:p-4 pt-1 sm:pt-1">
        <div>
          <div className="flex items-center justify-between mb-2">
            {isGenerating ? (
              <div className="flex items-center space-x-1.5 text-amber-700 bg-amber-50 px-2 py-1 rounded-lg">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span className="text-[10px] sm:text-xs font-medium">Generating...</span>
              </div>
            ) : (
              <div className="flex items-center space-x-1.5 text-emerald-700 bg-emerald-50 px-2 py-1 rounded-lg">
                <Target className="w-3 h-3" />
                <span className="text-[10px] sm:text-xs font-medium">{plan.duration_weeks}w &middot; 7d/wk</span>
              </div>
            )}
            {!canEdit && (
              <div className="text-[9px] text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                By {plan.created_by_role || 'admin'}
              </div>
            )}
          </div>
          {!isGenerating && !isFailed && (
            <div data-testid={`plan-progress-${plan.id}`} className="space-y-1">
              <div className="flex items-center justify-between text-[9px] sm:text-[10px]">
                <span className="text-gray-500 font-medium">Progress</span>
                <span className={`font-bold ${progressPct >= 100 ? 'text-emerald-600' : progressPct > 0 ? 'text-blue-600' : 'text-gray-400'}`}>
                  {progressPct}%
                </span>
              </div>
              <Progress value={progressPct} className="h-1.5" />
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function EmptyPlansState({ onGenerateClick }) {
  return (
    <div className="col-span-full text-center py-12">
      <div className="bg-gradient-to-br from-emerald-100 to-teal-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-3">
        <ClipboardList className="w-8 h-8 text-emerald-600" />
      </div>
      <p className="text-gray-600 mb-3 text-base font-medium">No training plans yet</p>
      <p className="text-gray-500 mb-4 text-sm">Complete the assessment to generate your personalized plan</p>
      <Button
        data-testid="generate-first-plan-btn"
        onClick={onGenerateClick}
        className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 shadow-lg"
      >
        <ClipboardList className="w-4 h-4 mr-2" />
        Complete Assessment
      </Button>
    </div>
  );
}

